/**
 * Admin · Metricool Settings
 *
 * Single source of truth for the Pinterest scheduling integration. The
 * admin uses this page to:
 *   1. Confirm Metricool API credentials are wired (`/admin/metricool/status`).
 *   2. Pick which Metricool brand (`blogId`) to schedule pins on.
 *   3. Map each of our 5 internal boards (canonical Buyer's Leverage names)
 *      to the actual Metricool Pinterest board IDs.
 *
 * After this mapping is set, the "Send to Pinterest" button on each
 * content package will route each generated pin to its auto-assigned
 * (or admin-overridden) board.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Loader2, Check, AlertCircle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    adminMetricoolStatus,
    adminMetricoolSelectBlog,
    adminMetricoolListPinterestBoards,
    adminMetricoolGetBoardMappings,
    adminMetricoolPutBoardMappings,
} from "@/lib/api";

export default function AdminMetricoolSettings() {
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [boards, setBoards] = useState([]);
    const [boardsLoading, setBoardsLoading] = useState(false);
    const [boardsError, setBoardsError] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [mappings, setMappings] = useState({});
    const [savingBlog, setSavingBlog] = useState(false);
    const [savingMappings, setSavingMappings] = useState(false);

    const loadStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            const s = await adminMetricoolStatus();
            setStatus(s);
            setCatalog(s.boards_catalog || []);
        } catch (e) {
            toast.error(`Failed to load status: ${e?.response?.data?.detail || e.message}`);
            setStatus({ configured: false });
        } finally {
            setStatusLoading(false);
        }
    }, []);

    const loadPinterestBoards = useCallback(async () => {
        if (!status?.configured || !status?.selected_blog_id) {
            setBoards([]);
            return;
        }
        setBoardsLoading(true);
        setBoardsError(null);
        try {
            const r = await adminMetricoolListPinterestBoards();
            setBoards(r.boards || []);
        } catch (e) {
            setBoardsError(e?.response?.data?.detail || e.message);
            setBoards([]);
        } finally {
            setBoardsLoading(false);
        }
    }, [status]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const s = await adminMetricoolStatus();
                if (alive) {
                    setStatus(s);
                    setCatalog(s.boards_catalog || []);
                }
            } catch (e) {
                if (alive) {
                    toast.error(`Failed to load status: ${e?.response?.data?.detail || e.message}`);
                    setStatus({ configured: false });
                }
            } finally {
                if (alive) setStatusLoading(false);
            }
            try {
                const m = await adminMetricoolGetBoardMappings();
                if (alive) {
                    setMappings(m.mappings || {});
                    if (m.boards_catalog) setCatalog(m.boards_catalog);
                }
            } catch {
                /* swallow — fresh installs have no mappings yet */
            }
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!status?.configured || !status?.selected_blog_id) {
            return;
        }
        let alive = true;
        (async () => {
            await Promise.resolve();
            if (!alive) return;
            setBoardsLoading(true);
            setBoardsError(null);
            try {
                const r = await adminMetricoolListPinterestBoards();
                if (alive) setBoards(r.boards || []);
            } catch (e) {
                if (alive) {
                    setBoardsError(e?.response?.data?.detail || e.message);
                    setBoards([]);
                }
            } finally {
                if (alive) setBoardsLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [status?.configured, status?.selected_blog_id]);

    const handleSelectBlog = async (blogId) => {
        setSavingBlog(true);
        try {
            await adminMetricoolSelectBlog(blogId);
            toast.success("Brand selected. Loading Pinterest boards…");
            await loadStatus();
        } catch (e) {
            toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setSavingBlog(false);
        }
    };

    const handleMapBoard = (internalKey, metricoolBoardId) => {
        setMappings((prev) => ({ ...prev, [internalKey]: metricoolBoardId }));
    };

    const handleSaveMappings = async () => {
        setSavingMappings(true);
        try {
            const cleaned = {};
            for (const [k, v] of Object.entries(mappings)) {
                if (v) cleaned[k] = v;
            }
            await adminMetricoolPutBoardMappings(cleaned);
            toast.success(`Saved ${Object.keys(cleaned).length} board mapping(s).`);
        } catch (e) {
            toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setSavingMappings(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-bl-bg text-bl px-4 py-6 sm:px-8"
            data-testid="admin-metricool-settings"
        >
            <div className="max-w-4xl mx-auto space-y-6">
                <header className="space-y-2">
                    <Link
                        to="/admin/content"
                        className="inline-flex items-center gap-1 text-xs text-bl-muted hover:text-bl"
                    >
                        <ChevronLeft className="h-3 w-3" /> Back to Content Engine
                    </Link>
                    <div className="flex items-end justify-between gap-3 flex-wrap">
                        <div>
                            <h1 className="font-display font-semibold text-3xl tracking-tight">
                                Metricool Settings
                            </h1>
                            <p className="text-sm text-bl-muted">
                                Configure how Buyer&apos;s Leverage schedules Pinterest pins via Metricool.
                            </p>
                        </div>
                        <Link
                            to="/admin/metricool/performance"
                            className="bl-btn-ghost text-xs gap-1.5 border border-bl-line"
                            data-testid="nav-pinterest-performance"
                        >
                            <RefreshCw className="h-3 w-3" />
                            Pinterest Performance
                        </Link>
                    </div>
                </header>

                {/* Connection status */}
                <section
                    className="bl-card p-5 space-y-3"
                    data-testid="metricool-connection-status"
                >
                    <div className="flex items-center justify-between gap-3">
                        <h2 className="font-display font-semibold text-lg">Connection</h2>
                        <button
                            type="button"
                            onClick={loadStatus}
                            disabled={statusLoading}
                            className="bl-btn-ghost text-xs gap-1.5"
                            data-testid="metricool-status-refresh"
                        >
                            {statusLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            Refresh
                        </button>
                    </div>
                    {statusLoading ? (
                        <div className="flex items-center gap-2 text-sm text-bl-muted">
                            <Loader2 className="h-4 w-4 animate-spin" /> Checking credentials…
                        </div>
                    ) : !status?.configured ? (
                        <div className="text-sm text-[var(--bl-amber)] flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <div>
                                Metricool is not configured. Add{" "}
                                <code className="bg-bl-bg/40 px-1 rounded">METRICOOL_USER_TOKEN</code> and{" "}
                                <code className="bg-bl-bg/40 px-1 rounded">METRICOOL_USER_ID</code> to{" "}
                                <code>backend/.env</code> and restart the backend.
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-[var(--bl-green)] flex items-center gap-2">
                            <Check className="h-4 w-4" />
                            Metricool credentials wired · {status?.brands?.length || 0} brand(s) discovered.
                        </div>
                    )}
                </section>

                {/* Brand selection */}
                {status?.configured && (status?.brands?.length || 0) > 0 && (
                    <section
                        className="bl-card p-5 space-y-3"
                        data-testid="metricool-brand-selection"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h2 className="font-display font-semibold text-lg">Brand · Blog</h2>
                                <p className="text-xs text-bl-muted">
                                    Pick the Metricool brand whose Pinterest profile you want pins scheduled on.
                                </p>
                            </div>
                            {status?.error && (
                                <div
                                    className="text-[11px] text-[var(--bl-amber)] flex items-start gap-1.5 max-w-xs"
                                    data-testid="metricool-brand-warning"
                                >
                                    <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                    <span>
                                        Metricool brand-listing API is slow or unreachable — showing your persisted brand.
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            {(status.brands || []).map((b) => {
                                const blogId = String(b.id || b.blogId || "");
                                const isSelected = String(status.selected_blog_id || "") === blogId;
                                return (
                                    <button
                                        type="button"
                                        key={blogId}
                                        onClick={() => handleSelectBlog(blogId)}
                                        disabled={savingBlog || isSelected}
                                        className={`w-full text-left p-3 rounded border ${isSelected ? "border-bl-primary bg-bl-primary/10" : "border-bl/30 hover:border-bl/60"} transition-colors`}
                                        data-testid={`metricool-brand-${blogId}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-semibold text-bl">
                                                    {b.label || b.title || b.name || `Brand ${blogId}`}
                                                </div>
                                                <div className="text-[11px] text-bl-muted font-mono">
                                                    blogId · {blogId}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <span className="text-bl-primary text-xs flex items-center gap-1">
                                                    <Check className="h-3 w-3" /> Active
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Board mapping */}
                {status?.configured && status?.selected_blog_id && (
                    <section
                        className="bl-card p-5 space-y-4"
                        data-testid="metricool-board-mapping"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h2 className="font-display font-semibold text-lg">Board mapping</h2>
                                <p className="text-xs text-bl-muted mt-0.5">
                                    Pair each of the 5 internal Buyer&apos;s Leverage boards with a real Pinterest board on Metricool.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={loadPinterestBoards}
                                disabled={boardsLoading}
                                className="bl-btn-ghost text-xs gap-1.5"
                                data-testid="metricool-pinterest-boards-refresh"
                            >
                                {boardsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                Reload boards
                            </button>
                        </div>
                        {boardsError && (
                            <div
                                className="text-xs text-[var(--bl-rust)] flex items-start gap-2 p-2 border border-[var(--bl-rust)]/30 rounded"
                                data-testid="metricool-pinterest-boards-error"
                            >
                                <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
                                <div>
                                    Couldn&apos;t list Pinterest boards from Metricool: {boardsError}
                                    <div className="text-bl-muted mt-1">
                                        Confirm your Pinterest account is connected inside Metricool, then click <em>Reload boards</em>.
                                    </div>
                                </div>
                            </div>
                        )}
                        {!boardsError && boards.length === 0 && !boardsLoading && (
                            <div
                                className="text-xs text-bl-muted p-2 border border-bl/30 rounded"
                                data-testid="metricool-pinterest-boards-empty"
                            >
                                Metricool didn&apos;t return any Pinterest boards (their public API doesn&apos;t expose a list endpoint reliably). Paste each board&apos;s Pinterest ID into the inputs below — see the help card at the bottom for how to find it.
                            </div>
                        )}

                        <div className="space-y-3">
                            {catalog.map((internalBoard) => (
                                <div
                                    key={internalBoard.key}
                                    className="grid grid-cols-1 sm:grid-cols-[1fr,1fr] gap-3 items-start"
                                    data-testid={`metricool-mapping-row-${internalBoard.key}`}
                                >
                                    <div>
                                        <div className="font-semibold text-sm text-bl">{internalBoard.name}</div>
                                        <div className="text-[11px] text-bl-muted mt-0.5">{internalBoard.description}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-bl-muted mt-1 font-mono">
                                            key · {internalBoard.key}
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        {boards.length > 0 && (
                                            <select
                                                value={mappings[internalBoard.key] || ""}
                                                onChange={(e) => handleMapBoard(internalBoard.key, e.target.value)}
                                                className="w-full text-sm border border-bl/30 rounded px-2 py-1.5 bg-bl-bg text-bl"
                                                data-testid={`metricool-mapping-select-${internalBoard.key}`}
                                            >
                                                <option value="">— Pick from detected Metricool boards —</option>
                                                {boards.map((b) => {
                                                    const bid = String(b.id || b.boardId || b.uri || "");
                                                    const label = b.name || b.title || bid;
                                                    return (
                                                        <option key={bid} value={bid}>
                                                            {label}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        )}
                                        <input
                                            type="text"
                                            value={mappings[internalBoard.key] || ""}
                                            onChange={(e) => handleMapBoard(internalBoard.key, e.target.value.trim())}
                                            placeholder="Paste Pinterest board ID (e.g. 1102593373842231929)"
                                            className="w-full text-sm border border-bl/30 rounded px-2 py-1.5 bg-bl-bg text-bl font-mono"
                                            data-testid={`metricool-mapping-input-${internalBoard.key}`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div
                            className="bl-card p-3 text-[11px] text-bl-muted border-l-4 border-l-bl-primary"
                            data-testid="metricool-board-id-help"
                        >
                            <strong className="text-bl">How to find a Pinterest board ID:</strong>
                            <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                                <li>Open your board in a browser (e.g. <code>pinterest.com/your-handle/repair-costs</code>).</li>
                                <li>Right-click → <em>View Page Source</em> → search for <code>boardId</code> — it&apos;s a 16+ digit number.</li>
                                <li>Alternative: schedule any pin to that board manually inside Metricool once; the board ID will then appear in the auto-detected dropdown above the next time you reload boards.</li>
                            </ol>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={handleSaveMappings}
                                disabled={savingMappings}
                                className="bl-btn-primary text-sm gap-1.5"
                                data-testid="metricool-save-mappings"
                            >
                                {savingMappings ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                                Save mappings
                            </button>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
